<template>
  <div class="description-item">
    <span class="description-item__title">{{ title }}:</span>
    <span
      v-if="hasContentSlot"
      class="description-item__content"
      :style="preLine ? { whiteSpace: 'pre-line' } : {}"
    >
      <slot name="content" />
    </span>

    <span
      v-else-if="hasTextContent"
      class="description-item__content"
      :style="preLine ? { whiteSpace: 'pre-line' } : {}"
    >
      <template v-if="convertedContent.length === 0">{{ content }}</template>

      <!-- CONTENT WITCH MEDIA CONTENT -->
      <span
        v-else
        v-for="(item, index) in convertedContent"
        :key="`description-content_${index}`"
      >
        <!-- PRODUCT CODE -->
        <ProductCode
          v-if="item.type === 'pCode'"
          :slug="item.body"
          :code="item.body"
        />

        <!-- LINKS -->
        <a
          v-else-if="item.type === 'link'"
          :href="getHref(item.body)"
          target="_blank"
        >
          {{ item.body }}
        </a>

        <!-- SIMPLE TEXT -->
        <template v-else>{{ item.body }}</template>
      </span>
    </span>

    <!-- eslint-disable -->
    <span
      v-else
      class="description-item__content description-item__content--placeholder"
      :style="preLine ? { whiteSpace: 'pre-line' } : {}"
    >
      {{ emptyContentPlaceholder }}
    </span>
    <!-- eslint-enable -->
  </div>
</template>

<script lang="ts">
import ProductCode from "@/components/Common/ProductCode"

export default {
  name: "DescriptionItem",

  props: {
    title: String,
    content: String,
    emptyContentPlaceholder: {
      type: String,
      default: "–"
    },
    preLine: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    hasContentSlot(): boolean {
      return !!this.$slots["content"]
    },

    hasTextContent(): boolean {
      return this.content && this.content.length > 0
    },

    convertedContent(): Array<any> {
      const str = this.content
      const links = str.match(this.allLinkPattern)
      const pCodes = str.match(this.pCodeTest)

      if (!pCodes && !links) return []

      let results = []

      if (links) {
        let lastIndex = 0

        links.forEach((link) => {
          const linkIndex = str.indexOf(link, lastIndex)

          results.push({
            type: "link",
            startIndex: linkIndex,
            endIndex: linkIndex + link.length,
            result: link
          })

          lastIndex = linkIndex + link.length
        })
      }

      if (pCodes) {
        let lastIndex = 0

        pCodes.forEach((link) => {
          const linkIndex = str.indexOf(link, lastIndex)

          results.push({
            type: "pCode",
            startIndex: linkIndex,
            endIndex: linkIndex + link.length,
            result: link
          })

          lastIndex = linkIndex + link.length
        })
      }

      results.sort((a, b) => a.startIndex - b.startIndex)

      let skipNextCodes = 0

      results = results.filter((result) => {
        if (result.type === "link") {
          const matchAll = result.result.match(this.pCodeTest)

          skipNextCodes = matchAll ? matchAll.length : 0
        }

        if (result.type === "pCode" && skipNextCodes) {
          skipNextCodes -= 1

          return false
        }

        return true
      })

      const mediaContent = []

      results.forEach((item, i, arr) => {
        // Adding string before first media content
        if (i === 0 && item.startIndex !== 0) {
          mediaContent.push({
            type: "string",
            body: str.slice(0, item.startIndex)
          })
        }

        // Adding string between previous media content
        if (i !== 0 && arr[i - 1].endIndex !== item.startIndex) {
          mediaContent.push({
            type: "string",
            body: str.slice(arr[i - 1].endIndex, item.startIndex)
          })
        }

        // Adding media content
        mediaContent.push({
          type: item.type,
          body: item.result
        })

        // Adding string after last media content
        if (arr.length - 1 === i && item.endIndex !== str.length) {
          mediaContent.push({
            type: "string",
            body: str.slice(item.endIndex, str.length)
          })
        }
      })

      return mediaContent
    }
  },

  data() {
    return {
      pCodeTest: /(p|P)0[a-zA-Z0-9]{6}/g,
      allLinkPattern:
        /[(http(s)?):\\/\\/(www\\.)?a-zA-Z0-9@:%._\\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/gim
    }
  },

  methods: {
    getHref(link: string): string {
      return link.includes("://") ? link : `http://${link}`
    }
  },

  components: {
    ProductCode
  }
}
</script>

<style lang="scss" scoped>
.description-item {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  &__title {
    margin-right: 8px;
    display: inline-block;
    color: rgba(0, 0, 0, 0.85);
  }

  &__content {
    word-break: break-word;
    hyphens: auto;

    &--placeholder {
      color: rgba(0, 0, 0, 0.25);
    }
  }
}
</style>
